import React, { useState, useEffect, useRef } from 'react';
import logger from '../utils/logger';
import * as appStorage from '../utils/appIndexedDB';
import './LoginPage.css';

const DOMAIN_STORAGE_KEY = 'mygenie_last_domain'; // 儲存最後選擇的網域

const DOMAIN_VALIDATION_MESSAGE = '請選擇網域'; // 未選網域時，瀏覽器顯示的驗證文案（可改這裡）
const USERNAME_VALIDATION_MESSAGE = '請填寫帳號'; // 帳號未填時顯示（可改這裡）
const PASSWORD_VALIDATION_MESSAGE = '請填寫密碼'; // 密碼未填時顯示（可改這裡）

const SENTRY_TEST_BUTTON_STORAGE_KEY = '__sentryTestButtonVisible';

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [domain, setDomain] = useState(''); // 新增域名狀態
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const domainSelectRef = useRef(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 820);
  // 測試按鈕預設隱藏，可從 DevTools 呼叫 __toggleSentryTestButton() 開關
  const [showSentryTestButton, setShowSentryTestButton] = useState(() => {
    try {
      return localStorage.getItem(SENTRY_TEST_BUTTON_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // 從 DevTools 呼叫 __toggleSentryTestButton() 顯示/隱藏測試按鈕，也可傳 true/false 指定狀態
  useEffect(() => {
    window.__toggleSentryTestButton = (visible) => {
      setShowSentryTestButton((prev) => {
        const next = typeof visible === 'boolean' ? visible : !prev;
        try {
          localStorage.setItem(SENTRY_TEST_BUTTON_STORAGE_KEY, String(next));
        } catch {
          // 忽略 localStorage 寫入失敗
        }
        return next;
      });
    };

    return () => {
      delete window.__toggleSentryTestButton;
    };
  }, []);

  // LoginPage 一律使用深色樣式，不套用淺色主題
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const prevTheme = root.getAttribute('data-theme');

    if (prevTheme === 'light') {
      root.removeAttribute('data-theme');
    }

    return () => {
      if (prevTheme === 'light') {
        root.setAttribute('data-theme', 'light');
      }
    };
  }, []);


  // 可選的域名列表（可根據實際需求修改）
  const domainOptions = [
    { value: '', label: '請選擇網域' },
    { value: 'PCS', label: 'PCS' },
    { value: 'TCS', label: 'TCS' },
  ];

  // 初始化時從 IndexedDB 讀取上次選擇的網域
  useEffect(() => {
    (async () => {
      const savedDomain = await appStorage.getItem(DOMAIN_STORAGE_KEY);
      const validDomains = domainOptions.map((o) => o.value).filter(Boolean);
      if (savedDomain && validDomains.includes(savedDomain)) {
        setDomain(savedDomain);
        logger.userAction('恢復上次選擇的網域', { domain: savedDomain });
      }
    })();
  }, []);


  // 帳號、密碼未填、未選網域時，自訂驗證訊息否則會出現瀏覽器預設「請選取一個清單中的項目」）
  useEffect(() => {
    const userEl = usernameRef.current;
    const pwdEl = passwordRef.current;
    const el = domainSelectRef.current;
    if (userEl) {
      userEl.setCustomValidity(!username.trim() ? USERNAME_VALIDATION_MESSAGE : '');
    }
    if (pwdEl) {
      pwdEl.setCustomValidity(!password.trim() ? PASSWORD_VALIDATION_MESSAGE : '');
    }
    if (!domain || !domain.trim()) {
      el.setCustomValidity(DOMAIN_VALIDATION_MESSAGE);
    } else {
      el.setCustomValidity('');
    }
  }, [username, password,domain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    logger.userAction('登入表單提交', {
      username: username.trim(),
      hasPassword: !!password.trim()
    });
    
    setIsLoading(true);
    setError('');

    // 基本驗證
    if (!username.trim() || !password.trim()) {
      logger.warn('LOGIN', '表單驗證失敗', {
        reason: '帳號或密碼為空'
      });
      setError('請輸入帳號和密碼');
      setIsLoading(false);
      return;
    }

    // 驗證網域為必填
    if (!domain || !domain.trim()) {
      logger.warn('LOGIN', '表單驗證失敗', {
        reason: '網域為空'
      });
      setError('請選擇網域');
      setIsLoading(false);
      return;
    }

    try {
      // 呼叫登入 API（固定使用 AD 帳號登入）
      logger.userAction('開始登入流程', { authMode: 'ldap', domain: domain || '未選擇' });
      const loginParams = {
        username: username.trim(),
        password: password.trim(),
        authMode: 'ldap',
      };
      // 如果有選擇域名，則加入參數
      if (domain && domain.trim()) {
        loginParams.domain = domain.trim();
      }
      const result = await onLogin(loginParams);

      if (result.success) {
        await appStorage.setItem(DOMAIN_STORAGE_KEY, domain.trim());
        logger.success('LOGIN', '登入成功', {
          username: username.trim(),
          domain: domain.trim(),
          message: result.message
        });
      } else {
        // 登入失敗，依 result.message 顯示對應訊息
        const msg = result.message || '';
        let displayMessage = result.message || '登入失敗';
        if (msg.includes('找不到使用者') || msg.includes('data 0')) {
          displayMessage = '帳號不存在，請再試一次';
        } else if (msg.includes('data 52e')) {
          displayMessage = '密碼不正確，請再試一次';
        } else if (msg.includes('data 775')) {
          displayMessage = '您已輸入錯誤密碼5次，帳號已被鎖定';
        } else if (result.success === false) {
          displayMessage = '登入失敗。請確認帳號輸入正確，並檢查所選網域是否符合。';
        }
        logger.warn('LOGIN', '登入失敗', {
          username: username.trim(),
          message: result.message
        });
        setError(displayMessage|| '登入失敗');
      }
    } catch (err) {
      logger.error('LOGIN', '登入過程發生異常', {
        username: username.trim(),
        error: err.message
      });
      setError('登入失敗，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`login-page ${isMobile ? 'mobile' : ''}`}>
      <div className={`login-container ${isMobile ? 'mobile' : ''}`}>
        {/* 背景層 */}
        <div className="login-bg-overlay"></div>
        <div className="login-scan-line"></div>
        <div className="login-ghost-text">INTELLIGENCE</div>


        <div className="login-header">
          <img 
            src="/logo-icon.png" 
            alt="Logo" 
            className="login-logo"
            width={60}
          />
          {/* 主內容區 */}
          <div className="login-content-wrapper">
            <div className="login-intro-label">SYSTEM INITIALIZED</div>
            
            <div className="login-brand-focus">
              <div className="login-brand-outline">MyBro</div>
              <div className="login-brand-main">MyBro</div>
              
              {/* 科技感副標題：黑玻璃質感 */}
              <div className="login-subtitle-box">
                <div className="login-subtitle-text"> 助理小哥</div>
              </div>
            </div>

          </div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">帳號</label>
            <input
              ref={usernameRef}
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="請輸入您的帳號(非Email)"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">密碼</label>
            <input
              ref={passwordRef}
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="請輸入您的密碼"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="domain">網域</label>
            <select
              ref={domainSelectRef}
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="auth-mode-select"
              disabled={isLoading}
              title=""
            >
              {domainOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? '登入中...' : '登入'}
          </button>

          {showSentryTestButton && process.env.REACT_APP_SENTRY_ENVIRONMENT !== 'production' && (
            <button
              type="button"
              className="login-button"
              onClick={() => {
                throw new Error('Sentry 測試錯誤：LoginPage 測試按鈕觸發');
              }}
            >
              測試 Sentry 錯誤
            </button>
          )}
        </form>

      </div>
    </div>
  );
};

export default LoginPage;
